/**
 * SavedViewsMenu — list, load, save, delete user/company saved views.
 *
 * Read-only mode (`readOnly` / `shareToken`): hides the save/delete UI and
 * fetches from the PUBLIC endpoint instead. Used by the share view to let
 * link consumers apply company-published Gantt presets without granting
 * write access.
 */
import { useCallback, useEffect, useState } from "react";
import { X, Save, Trash2, Users, User } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { http } from "../lib/api";
import { useI18n } from "../context/I18nContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Same hardened public axios as SharedView — never touches XSRF / cookies
// so the menu works inside Power BI's sandboxed iframe.
const publicHttp = axios.create({ withCredentials: false, withXSRFToken: false, xsrfCookieName: null });

export default function SavedViewsMenu({ open, onClose, screen = "gantt", currentState, onApply, canSaveCompany, readOnly = false, shareToken = null }) {
  const { t } = useI18n();
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [scope, setScope] = useState("user");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      if (readOnly && shareToken) {
        // Public endpoint — returns only company-scope views for the
        // link's owning company, suitable for anonymous consumption.
        const r = await publicHttp.get(`${API}/share/${shareToken}/saved-views`);
        setViews(r.data.views || []);
      } else {
        const r = await http.get(`/saved-views?screen=${screen}`);
        setViews(r.data.views || []);
      }
    } finally { setLoading(false); }
  }, [screen, readOnly, shareToken]);
  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const onSave = async () => {
    if (!name.trim()) { toast.error(t("saved_views_name_required")); return; }
    try {
      await http.post("/saved-views", { name: name.trim(), screen, scope, state: currentState || {} });
      toast.success(t("saved_views_saved"));
      setName("");
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const onLoad = (v) => { onApply?.(v.state); toast.success(`${t("saved_views_loaded")}: ${v.name}`); onClose?.(); };

  const onDel = async (id) => {
    if (!window.confirm(t("saved_views_confirm_delete"))) return;
    try { await http.delete(`/saved-views/${id}`); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/40 p-4" data-testid="saved-views-menu">
      <div className="bg-white border border-zinc-200 rounded-md shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[80vh]">
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <h3 className="font-heading text-base font-semibold">{t("saved_views_title")}</h3>
            {readOnly && <p className="text-[10px] font-mono text-zinc-500">{t("saved_views_read_only_subtitle")}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-zinc-100"><X size={16} /></button>
        </div>
        {!readOnly && (
          <div className="p-4 border-b border-zinc-100 space-y-2">
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("saved_views_name_placeholder")}
              className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm" data-testid="sv-name-input" />
            <div className="flex items-center justify-between gap-2">
              <label className="inline-flex items-center gap-1.5 text-xs">
                <input type="radio" name="scope" value="user" checked={scope === "user"} onChange={() => setScope("user")} className="text-cs-accent" data-testid="sv-scope-user" />
                <User size={12} /> {t("saved_views_scope_user")}
              </label>
              <label className={`inline-flex items-center gap-1.5 text-xs ${!canSaveCompany ? "opacity-50" : ""}`}>
                <input type="radio" name="scope" value="company" checked={scope === "company"} onChange={() => setScope("company")} disabled={!canSaveCompany} className="text-cs-accent" data-testid="sv-scope-company" />
                <Users size={12} /> {t("saved_views_scope_company")}
              </label>
              <button onClick={onSave} className="ml-auto px-3 py-1.5 bg-cs-accent text-white text-xs font-semibold rounded-md hover:bg-cs-accentHover inline-flex items-center gap-1" data-testid="sv-save-btn">
                <Save size={12} /> {t("saved_views_save")}
              </button>
            </div>
          </div>
        )}
        <div className="overflow-y-auto flex-1 p-2 scrollbar-thin">
          {loading ? <div className="text-xs text-zinc-500 p-3">{t("saved_views_loading")}</div> : views.length === 0 ? (
            <div className="text-xs text-zinc-500 p-3 text-center">{readOnly ? t("saved_views_empty_public") : t("saved_views_empty")}</div>
          ) : views.map((v) => (
            <div key={v.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-50 rounded" data-testid={`sv-row-${v.id}`}>
              <button onClick={() => onLoad(v)} className="flex-1 text-left">
                <div className="text-xs font-medium text-zinc-900 truncate">{v.name}</div>
                <div className="text-[10px] font-mono text-zinc-500 truncate">
                  {v.scope === "company" ? t("saved_views_scope_label_company") : t("saved_views_scope_label_user")}{v.created_by_email ? ` · ${v.created_by_email}` : ""}
                </div>
              </button>
              {!readOnly && (v.is_mine || canSaveCompany) && (
                <button onClick={() => onDel(v.id)} className="p-1 text-zinc-400 hover:text-red-600">
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
